import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { useId } from "react";

import { cn } from "@/shared/lib";
import { BlockError } from "@/shared/ui/block-error";
import { Button } from "@/shared/ui/button";
import { Skeleton } from "@/shared/ui/skeleton";

import { useDrillsOverview } from "../../api/useDrillsOverview";
import { formatNumber } from "../../model/formatNumber";

// Задача серии занимает меньше минуты: на «около N минут» хватает округления вверх.
const MINUTES_PER_TASK = 0.8;

const WAITING = msg({
  message: "{pending, plural, one {# task is waiting} other {# tasks are waiting}}",
});
const WAITING_TEXT = msg({
  message:
    "From the mistakes of your analyses, about {minutes, plural, one {# minute} other {# minutes}}. A topic you missed yesterday comes back.",
});
const SOLVED = msg({ message: "Solved {answered} of {total}" });
const PASSED = msg({ message: "Series done: {points} of {total}" });
const STREAK = msg({
  message: "{days, plural, =0 {} one {# day in a row. } other {# days in a row. }}",
});
const MORE_LATER = msg({ message: "New tasks appear after your next analysis." });
const MORE_NOW = msg({ message: "You can take another series." });

const GRADE_DOT = {
  right: "bg-good",
  close: "bg-warn",
  wrong: "bg-bad",
} as const;

// «Тренировка дня» на главной по макету design/prototype/DrillHome.dc.html: задачи ждут,
// серия начата, серия пройдена, на сегодня всё или разборов ещё нет. Число задач — то же,
// что на значке меню. Пока сводка грузится — заглушка на месте блока, колонка не прыгает.
export const DrillOfTheDay = () => {
  const { i18n, t } = useLingui();
  const headingId = useId();
  const overview = useDrillsOverview();
  const { data } = overview;
  if (overview.isError) {
    return (
      <BlockError
        title={t`Could not load the drill of the day`}
        retrying={overview.isFetching}
        onRetry={() => void overview.refetch()}
      />
    );
  }
  if (data === undefined) {
    return (
      <div className="flex min-h-48 flex-col gap-2.5 rounded-xl border border-border bg-surface px-5 py-5">
        <Skeleton className="h-3 w-28" />
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="mt-auto h-11 w-28 rounded-lg" />
      </div>
    );
  }
  const series = data.series;
  const started = series !== null && !series.finished && series.answered > 0;
  const passed = series !== null && series.finished;
  const strong = data.pending > 0;
  const streak = i18n._({ ...STREAK, values: { days: data.streak_days } });
  let title = t`Tasks from your matches`;
  let text = t`Analyze a match and its mistakes become your first tasks: what to buy and when, how to defend and what to take against whoever kills you.`;
  let cta: string | null = null;
  if (series !== null && started) {
    title = i18n._({ ...SOLVED, values: { answered: series.answered, total: series.total } });
    text = t`Pick up where you left off.`;
    cta = t`Continue`;
  } else if (series !== null && passed) {
    const right = series.grades.filter((grade) => grade === "right").length;
    const close = series.grades.filter((grade) => grade === "close").length;
    title = i18n._({
      ...PASSED,
      values: {
        points: formatNumber(right + close / 2, i18n.locale, { maximumFractionDigits: 1 }),
        total: series.total,
      },
    });
    text = streak + i18n._(data.available > 0 ? MORE_NOW : MORE_LATER);
    cta = data.available > 0 ? t`Another series` : t`Topics`;
  } else if (strong) {
    title = i18n._({ ...WAITING, values: { pending: data.pending } });
    text = i18n._({
      ...WAITING_TEXT,
      values: { minutes: Math.max(1, Math.ceil(data.pending * MINUTES_PER_TASK)) },
    });
    cta = t`Start`;
  } else if (data.has_analyses) {
    title = t`That's all for today`;
    text = streak + i18n._(MORE_LATER);
    cta = t`Topics`;
  }
  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        "flex flex-col gap-2.5 rounded-xl border px-5 py-5",
        strong || started ? "border-border-strong bg-surface-raised" : "border-border bg-surface",
      )}
    >
      <span className="text-xs tracking-[0.08em] text-accent uppercase">{t`Drill of the day`}</span>
      <h2 id={headingId} className="text-lg font-semibold">
        {title}
      </h2>
      {series !== null && (started || passed) && (
        <div aria-hidden="true" className="flex gap-1.5">
          {Array.from({ length: series.total }, (_, index) => {
            const grade = series.grades[index];
            return (
              <span
                key={index}
                className={cn(
                  "block size-3 rounded-full",
                  grade === undefined ? "bg-sidebar-active" : GRADE_DOT[grade],
                )}
              />
            );
          })}
        </div>
      )}
      <p className="text-sm text-muted">{text}</p>
      {cta !== null && (
        <Button
          asChild
          size="lg"
          variant={strong || started ? "default" : "outline"}
          className="mt-1 min-h-11 self-start no-underline"
        >
          <Link to="/drills">{cta}</Link>
        </Button>
      )}
    </section>
  );
};
